import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { getErrorMessage } from "@/api/client";
import ForgotPinDialog from "@/components/ForgotPinDialog";
import { LockKeyhole, ShieldAlert } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [employeeId, setEmployeeId] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(employeeId.trim().toUpperCase(), pin.trim());
      navigate("/", { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#090D16] flex-col justify-between p-12 overflow-hidden">
        <img
          src="/team-sd-login.png"
          alt="Team SD — S.D. Infratech field team in front of a Sunward piling rig"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#090D16] via-[#090D16]/70 to-[#090D16]/30" />
        <div className="relative z-10">
          <p className="text-white font-bold text-2xl tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            S.D. Infratech Pvt. Ltd.
          </p>
          <p className="text-amber-500 font-mono text-xs uppercase tracking-widest mt-1">Unified ERP · Web Console</p>
        </div>
        <div className="relative z-10 text-slate-300 max-w-md">
          <p className="text-3xl font-extrabold text-white leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            One system. Every site. <span className="text-amber-500">Every machine.</span>
          </p>
          <p className="mt-4 text-sm text-slate-400">
            The same live records your field teams use on mobile — now on a desktop console built for Masters, Operations, Payment Desk and Reports.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-slate-50 px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 text-center">
            <p className="text-slate-900 font-bold text-xl" style={{ fontFamily: "Outfit, sans-serif" }}>
              S.D. Infratech Pvt. Ltd.
            </p>
            <p className="text-amber-600 font-mono text-xs uppercase tracking-widest mt-1">Unified ERP</p>
          </div>

          <div className="mb-8">
            <div className="h-11 w-11 rounded-lg bg-amber-600 flex items-center justify-center mb-4">
              <LockKeyhole className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Sign in
            </h1>
            <p className="text-sm text-slate-500 mt-1">Use your Employee ID and 4-digit PIN.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="employee-id">Employee ID</Label>
              <Input
                id="employee-id"
                data-testid="login-emp-id-input"
                placeholder="e.g. HO-1101"
                autoCapitalize="characters"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pin">4-Digit PIN</Label>
              <Input
                id="pin"
                data-testid="login-pin-input"
                type="password"
                inputMode="numeric"
                maxLength={4}
                placeholder="••••"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                required
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2.5" data-testid="login-error-message">
                <ShieldAlert className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full bg-amber-600 hover:bg-amber-700 text-white h-10">
              {loading ? "Signing in..." : "Sign In"}
            </Button>

            <button
              type="button"
              data-testid="login-forgot-pin-link"
              onClick={() => setForgotOpen(true)}
              className="w-full text-center text-sm text-slate-500 hover:text-amber-600 transition-colors"
            >
              Forgot PIN?
            </button>
          </form>
        </div>
      </div>

      <ForgotPinDialog open={forgotOpen} onOpenChange={setForgotOpen} />
    </div>
  );
}
